import _, { keyBy } from "lodash";
import * as React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Alert, Button, Card, Group, Loader, Modal, Select, Stack, Tabs, Text, Tooltip } from "@mantine/core";
import { removeErrorFromGame, updateGame } from "../../data/actions/gameActions";
import GameApi from "../../data/api/gameApi";
import { IState } from "../../data/reducers/state";
import { IAssociation } from "../../types/association/Association";
import { ICompetition } from "../../types/competition/Competition";
import { IGame } from "../../types/game/Game";
import { IGameConfiguration } from "../../types/game/GameConfiguration";
import { ICascadedPayrate } from "../../types/payrates/PayRate";
import { TagType } from "../../types/tags/tag";
import { ITeam } from "../../types/team/Team";
import { IVenue } from "../../types/venue/Venue";
import { toDateTime } from "../../util/date+helper";
import { getGameKey } from "../../util/gamehelper";
import { CompetitionLink } from "../common/Links";
import { CenteredLoader } from "../common/loader";
import { SSTable } from "../common/TableData";
import OfficialsRoster from "../management/roster/OfficialsRoster";
import PayrateEditor from "../payrates/PayrateEditor";
import OfficialsModeSelector from "./OfficialsMode";

export interface IGameManagerProps {
  gameId: string;
  shiftId?: string;
  sessionId: string;

  onDelete: Function;

  // from state
  game?: IGame;
  associations: IAssociation[];
  config: IGameConfiguration | null;
  competition?: ICompetition;
  venue?: IVenue;
  teams?: { [key: string]: ITeam };
  gameAppointmentsWithPosition: { _id: string; position: string }[];
  actions: {
    updateGame: (game: Partial<IGame>) => Promise<null>;
    removeErrorFromGame: (gameId: string) => void;
  };
}

export interface GameManagerState {
  tab?: string;
  payrateLoading: boolean;
  payrateError?: string | null;
  effectivePayrate: ICascadedPayrate | null;
  payrateSources: { _id?: string; name?: string; associationId?: string }[];
  showPayrateModal: boolean;
}

class GameManager extends React.PureComponent<
  IGameManagerProps,
  GameManagerState
> {
  constructor(props, context) {
    super(props, context);

    this.state = {
      tab: "Details",
      payrateLoading: false,
      payrateError: null,
      effectivePayrate: null,
      payrateSources: [],
      showPayrateModal: false,
    };
    this.onValueChange = this.onValueChange.bind(this);
    this.deleteGame = this.deleteGame.bind(this);
    this.toggle = this.toggle.bind(this);
  }

  onValueChange(e) {
    const t = e.currentTarget;
    if (!t.name || !this.props.actions) {
      throw new Error(
        "An internal error has occurred, there is no name property on the currently edited field",
      );
    }
    const parts: string[] = t.name.split(".");
    const { value } = t;

    const gameToUpdate: Partial<IGame> = this.getNestedChange({}, parts, value);
    gameToUpdate._id = this.props.gameId;
    this.props.actions.updateGame(gameToUpdate).catch((er) => alert(er));
  }

  getNestedChange(obj: object, keyParts: string[], newValue: any): object {
    if (keyParts.length === 0) {
      throw Error(
        "An internal error has occurred: Invalid number of key parts",
      );
    }
    const key = keyParts[0];
    if (keyParts.length === 1) return { ...obj, [key]: newValue };

    const nested = obj[key] || {}; // create parts of the object we don't have
    return {
      ...obj,
      [key]: this.getNestedChange(nested, keyParts.slice(1), newValue),
    };
  }

  toggle(tabKey) {
    this.setState({ tab: tabKey });
  }

  componentDidMount(): void {
    this.fetchEffectivePayrate();
  }

  componentDidUpdate(prevProps: Readonly<IGameManagerProps>) {
    const prevGame = prevProps.game;
    const nextGame = this.props.game;
    if (!prevGame && nextGame) {
      this.fetchEffectivePayrate();
      return;
    }
    if (
      prevGame &&
      nextGame &&
      (prevGame._id !== nextGame._id ||
        prevGame.payrate !== nextGame.payrate ||
        prevGame.mode !== nextGame.mode ||
        prevGame.duration !== nextGame.duration ||
        prevGame.date !== nextGame.date ||
        prevGame.venue !== nextGame.venue ||
        prevGame.court !== nextGame.court ||
        prevProps.sessionId !== this.props.sessionId)
    ) {
      this.fetchEffectivePayrate();
    }
  }

  fetchEffectivePayrate(): void {
    if (!this.props.gameId) return;
    this.setState({ payrateLoading: true, payrateError: null });
    GameApi.getEffectivePayrate(this.props.gameId, this.props.sessionId)
      .then((resp) => {
        this.setState({
          effectivePayrate: resp.data?.payrate || null,
          payrateSources: resp.data?.sources || [],
          payrateLoading: false,
        });
      })
      .catch((er) => {
        this.setState({
          payrateError: er?.message || "Unable to load payrate",
          effectivePayrate: null,
          payrateSources: [],
          payrateLoading: false,
        });
      });
  }

  deleteGame() {
    if (!this.props.game) throw new Error("An internal error has occurred");

    // check if game has referees
    if (this.props.game.persons && this.props.game.persons.length > 0) {
      if (
        !confirm(
          "game has multiple referees, are you sure you'd like to delete this game",
        )
      )
        return;
    }

    GameApi.deleteGame(this.props.gameId)
      .then(() => this.props.onDelete())
      .catch((er) => {
        alert(er.message);
      });
  }

  render() {
    const { game, actions, venue, competition, teams, associations } =
      this.props;

    if (!game) return <p>Loading ...</p>;

    const minValue = 10;
    const maxValue = 120;
    const intervalLength = 5;

    const team1 = teams && game.team1 in teams ? teams[game.team1].name : "";
    const team2 = teams && game.team2 in teams ? teams[game.team2].name : "";

    const { config } = this.props;
    const appointmentsByPosition = keyBy(
      this.props.gameAppointmentsWithPosition,
      "position",
    );
    const hasOfficials: boolean =
      this.props.gameAppointmentsWithPosition?.length > 0;

    const relevantFields: string[] =
      hasOfficials && config
        ? config.fields
          .filter((f) => f.identifier in appointmentsByPosition)
          .map((f) => f.identifier)
        : [];
    const { effectivePayrate, payrateSources, payrateLoading, payrateError, showPayrateModal } = this.state;
    const payrateNameList = (
      payrateSources.length > 0
        ? payrateSources.map((p) => p?.name)
        : effectivePayrate?.names || []
    ).filter((name): name is string => !!name);

    const modes = new Set(
      competition?.defaults?.payrates
        ?.map(
          (p) => p.tags?.filter((t) => t.type == TagType.gameMode)[0]?.value,
        )
        ?.filter((t) => !!t) || [],
    );
    if (game.mode) modes.add(game.mode);

    for (const assoc of this.props.associations || []) {
      if (!assoc.payrates) continue;
      for (const amode of assoc.payrates) {
        const mode = (amode?.tags || []).filter(
          (t) => t.type == TagType.gameMode,
        );
        if (mode.length > 0) modes.add(mode[0].value);
      }
    }

    const mode =
      modes.size <= 1 ? (
        game.mode || "default"
      ) : (
        <Select
          value={game.mode || "default"}
          name="mode"
          onChange={(value) => {
            const event = { currentTarget: { name: "mode", value } };
            this.onValueChange(event);
          }}
          data={Array.from(modes).map((m) => ({
            value: m,
            label: m?.toUpperCase()
          }))}
        />
      );

    const tabs = {
      Details: (
        <>
          <Card style={{ padding: "1rem" }}>
            <Tooltip 
              label={game._id} 
              position="bottom"
              multiline
              w={500}
            >
              <p style={{ color: "gray", cursor: "help" }}>
                OVERVIEW ({team1?.toUpperCase()} <em>vs</em>{" "}
                {team2?.toUpperCase()})
              </p>
            </Tooltip>
            <SSTable>
              <tbody>
                <tr>
                  <td>Date</td>
                  <td>
                    {toDateTime(game.date).toFormat("f")} ({game.duration}{" "}
                    minutes)
                  </td>
                </tr>
                <tr>
                  <td>Venue</td>
                  <td>
                    {venue ? venue.name : ""} -{game.court}
                  </td>
                </tr>
                <tr>
                  <td>Mode</td>
                  <td>{mode}</td>
                </tr>
                <tr>
                  <td style={{ paddingRight: "10px" }}>Competition</td>
                  <td>
                    {competition && (
                      <CompetitionLink competitionId={competition._id}>
                        {competition.name}
                      </CompetitionLink>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <Link to="/admin/payrates">Payrate</Link>
                  </td>
                  <td>
                    {payrateLoading ? (
                      <Loader size="sm" />
                    ) : payrateError ? (
                      <Text size="sm" c="red">
                        {payrateError}
                      </Text>
                    ) : (
                      <Group gap="sm">
                        <div>
                          <ul style={{ margin: 0 }}>
                            {payrateNameList.length > 0 ? payrateNameList.map((pr) => <li key={pr}>{pr}</li>) : (
                              <li style={{ listStyleType: "none", color: "gray" }}>No payrates found</li>
                            )}
                          </ul>
                        </div>
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => this.setState({ showPayrateModal: true })}
                          disabled={payrateNameList.length === 0}
                        >
                          View
                        </Button>
                      </Group>
                    )}
                  </td>
                </tr>
              </tbody>
            </SSTable>
          </Card>
          <Card style={{ padding: "1rem", marginTop: "1rem" }}>
            <p style={{ color: "gray" }}>GAME CONFIGURATIONS</p>

            {hasOfficials && (
              <p style={{ color: "gray" }}>
                You may have to remove officials from this game for all game
                game configurations to become available.
              </p>
            )}
            <OfficialsModeSelector
              name="referees.config"
              fields={relevantFields}
              mode={game.referees?.config || "twoPO"}
              onChange={this.onValueChange}
            />
            <br />
            <OfficialsRoster
              officials={game.referees}
              gameId={game._id}
              shiftId={this.props.shiftId}
              sessionId={this.props.sessionId}
              startDate={game.date}
              venue={game.venue}
            />
          </Card>
        </>
      ),
      Advanced: (
        <>
          <Alert color="red">
            Actions in this section might be destructive and irreversible!
          </Alert>
          <Card style={{ padding: "1rem" }}>
            <p style={{ color: "gray" }}>GAME DURATION</p>
            <p>
              This field will change the duration of the game (currently{" "}
              {game.duration} minutes).
            </p>
            <Stack gap="sm">
              {hasOfficials && (
                <p style={{ color: "gray" }}>
                  You must remove all officials from this game for this action
                  to become available
                </p>
              )}
              <Select
                name="duration"
                disabled={hasOfficials}
                onChange={(value) => {
                  const event = { currentTarget: { name: "duration", value } };
                  this.onValueChange(event);
                }}
                value={`${game.duration}`}
                data={_.range(minValue, maxValue, intervalLength).map((val) => ({
                  value: val.toString(),
                  label: `${val} minutes`
                }))}
              />
            </Stack>
          </Card>
          <Card style={{ padding: "1rem", marginTop: "1rem" }}>
            <p style={{ color: "gray" }}>DESTRUCTIVE ACTION</p>
            <p>
              This action will irreversibly delete the game. This action will be
              logged.
            </p>
            {hasOfficials && (
              <p style={{ color: "gray" }}>
                You must remove all officials from this game for this action to
                become available
              </p>
            )}
            <Button
              style={{ width: "50%", marginLeft: "auto" }}
              onClick={this.deleteGame}
              color="red"
              disabled={hasOfficials}
              variant="outline"
            >
              Delete game)
            </Button>
          </Card>
        </>
      ),
    };

    const hasError = !game.loading && !!game.message;

    return (
      <>
        <Tabs value={this.state.tab} onChange={this.toggle}>
          <Tabs.List>
            {Object.keys(tabs).map((k) => (
              <Tabs.Tab key={k} value={k}>
                {k}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          
          {Object.keys(tabs).map((k) => (
            <Tabs.Panel key={k} value={k} pt="md">
              {tabs[k]}
            </Tabs.Panel>
          ))}
        </Tabs>
        <Modal
          opened={showPayrateModal}
          onClose={() => this.setState({ showPayrateModal: false })}
          title="View Effective Payrate"
          size="xl"
        >
          {payrateLoading ? (
            <CenteredLoader />
          ) : payrateError ? (
            <Alert color="red">{payrateError}</Alert>
          ) : !effectivePayrate && payrateSources.length === 0 ? (
            <Text size="sm" c="dimmed">
              No payrate data available for this game.
            </Text>
          ) : (
            <Stack gap="sm">
              <div>
                <Text size="sm" c="dimmed">
                  Applied payrates (highest precedence first)
                </Text>
                <ul style={{ marginTop: "0.25rem" }}>
                  {payrateNameList.map((name) => <li key={name}>{name}</li>)}
                </ul>
              </div>
              <PayrateEditor
                payrate={effectivePayrate as any}
                readOnly
                onCancel={() => this.setState({ showPayrateModal: false })}
              />
            </Stack>
          )}
        </Modal>
        {game.loading && <CenteredLoader />}
        {hasError && (
          <Alert
            color="red"
            withCloseButton={!!actions}
            onClose={
              actions ? () => actions.removeErrorFromGame(game._id) : undefined
            }
          >
            {game.message}
          </Alert>
        )}
      </>
    );
  }
}

function mapStateToProps(
  state: IState,
  props: IGameManagerProps,
): Partial<IGameManagerProps> {
  const game: IGame = state.games[props.gameId] as IGame;
  if (!game) return {};

  const gameConfig = game.referees?.config;
  let gameAppointmentsWithPosition: { _id: string; position: string }[] = [];
  if (game) {
    gameAppointmentsWithPosition =
      state.appointments.byVenueDateCourt[
      getGameKey(game.date, game.venue, game.court)
      ] || [];
  }
  return {
    game,
    associations: game.associations
      .map((a) => state.associations.map[a])
      .filter((a) => a!),
    gameAppointmentsWithPosition,
    competition: state.competitions.map[game.competition],
    venue: state.venues.map[game.venue],
    teams: state.teams.map,
    config: state.gameConfigurations.map[gameConfig],
  };
}

function mapDispatchToProps(dispatch): Partial<IGameManagerProps> {
  return {
    actions: {
      updateGame: (game: Partial<IGame>) => dispatch(updateGame(game)),
      removeErrorFromGame: (gameId: string) =>
        dispatch(removeErrorFromGame(gameId)),
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(GameManager);
